/**
 * 🖼️ 圖片處理規則說明
 *
 * 【貼文圖片】等比縮放，不裁切，不放大
 * - Small: 600x600 限制（實際依原圖比例）
 * - Big: 1920x1080 限制（實際依原圖比例）
 *
 * 【封面圖片】等比縮放 + 裁切（3:1 比例）
 * - Big Cover: 1350x450
 * - Small Cover: 450x150
 *
 * 【頭像圖片】等比縮放（正方形，只指定寬度）
 * - Big Avatar: 300x300
 * - Small Avatar: 100x100
 *
 * 【圖片使用場景】
 * - 編輯模式預覽：url (small)
 * - 卡片列表顯示：url (small)
 * - 燈箱大圖：big_url (big)
 * - 燈箱縮圖：url (small) / thumbnail_url (影片)
 * - 鎖定狀態：thumbnail_url (blur，基於 small 生成)
 */

/**
 * 處理圖片尺寸和轉換
 * @param {File} file - 原始檔案
 * @param {Object} options - 配置選項
 * @param {Array} options.sizes - 需要的尺寸配置陣列，每個配置包含 width, height, prefix
 * @returns {Promise<Object>} - 返回處理後的檔案物件
 */
export const processImage = (file, options) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    // 讀檔、解碼、輸出任一步失敗都要 reject：少了任何一個，呼叫端的 await 會永遠不結束，
    // 上傳流程卡住且沒有任何提示（例如副檔名是 .jpg 但內容已損毀的檔案）
    reader.onerror = () => {
      reject(new Error('File reading failed'));
    };
    reader.onload = () => {
      img.onerror = () => {
        reject(new Error('Image loading failed'));
      };
      img.onload = () => {
        // onload 是事件 callback，裡面同步丟出的例外不會讓外層 Promise reject（例如 Safari 在
        // canvas 總記憶體超過上限時 getContext 回 null，下一行 drawImage 就會丟 TypeError），要自己接住
        try {
          const results = {};

          options.sizes.forEach(size => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // 等比縮放，保持比例，不放大
            let finalWidth = img.width;
            let finalHeight = img.height;

            // 只在圖片超過限制時才縮小
            const exceedsWidth = img.width > size.width;
            const exceedsHeight = size.height != null && img.height > size.height;
            if (exceedsWidth || exceedsHeight) {
              const widthRatio = size.width / img.width;
              const ratio = size.height != null
                ? Math.min(widthRatio, size.height / img.height)
                : widthRatio;

              finalWidth = Math.round(img.width * ratio);
              finalHeight = Math.round(img.height * ratio);
            }

            canvas.width = finalWidth;
            canvas.height = finalHeight;
            ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

            canvas.toBlob((blob) => {
              // 畫布無法輸出（例如超出瀏覽器的畫布上限）時 blob 為 null，
              // 不擋的話會產生內容為字串 "null" 的檔案並被上傳
              if (!blob) {
                reject(new Error('Failed to generate resized image'));
                return;
              }
              const processedFile = new File(
                [blob],
                `${size.prefix}_${file.name}`,
                { type: file.type }
              );

              results[size.prefix] = processedFile;

              // 當所有尺寸都處理完成時返回結果
              if (Object.keys(results).length === options.sizes.length) {
                resolve(results);
              }
            }, file.type);
          });
        } catch (error) {
          reject(error);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * StackBlur 算法 - 跨瀏覽器兼容的模糊實現
 * 直接操作像素，不依賴 ctx.filter（Safari 支援有問題）
 * @param {ImageData} imageData - Canvas ImageData
 * @param {number} radius - 模糊半徑
 */
const stackBlur = (imageData, radius) => {
  const pixels = imageData.data;
  const width = imageData.width;
  const height = imageData.height;

  if (radius < 1) return;

  const wm = width - 1;
  const hm = height - 1;
  const div = radius + radius + 1;

  const r = [];
  const g = [];
  const b = [];

  const vmin = [];
  const vmax = [];

  let rsum, gsum, bsum, p, p1, p2, yp, yi, yw;

  // 水平模糊
  yw = yi = 0;
  for (let y = 0; y < height; y++) {
    rsum = pixels[yw] * (radius + 1);
    gsum = pixels[yw + 1] * (radius + 1);
    bsum = pixels[yw + 2] * (radius + 1);

    for (let i = 1; i <= radius; i++) {
      p = yw + ((i > wm ? wm : i) << 2);
      rsum += pixels[p];
      gsum += pixels[p + 1];
      bsum += pixels[p + 2];
    }

    for (let x = 0; x < width; x++) {
      r[yi] = rsum;
      g[yi] = gsum;
      b[yi] = bsum;

      if (y === 0) {
        vmin[x] = Math.min(x + radius + 1, wm) << 2;
        vmax[x] = Math.max(x - radius, 0) << 2;
      }

      p1 = yw + vmin[x];
      p2 = yw + vmax[x];

      rsum += pixels[p1] - pixels[p2];
      gsum += pixels[p1 + 1] - pixels[p2 + 1];
      bsum += pixels[p1 + 2] - pixels[p2 + 2];

      yi++;
    }
    yw += width << 2;
  }

  // 垂直模糊
  for (let x = 0; x < width; x++) {
    yp = x;
    rsum = r[yp] * (radius + 1);
    gsum = g[yp] * (radius + 1);
    bsum = b[yp] * (radius + 1);

    for (let i = 1; i <= radius; i++) {
      yp = x + Math.min(i, hm) * width;
      rsum += r[yp];
      gsum += g[yp];
      bsum += b[yp];
    }

    yi = x << 2;
    for (let y = 0; y < height; y++) {
      pixels[yi] = Math.round(rsum / div / div);
      pixels[yi + 1] = Math.round(gsum / div / div);
      pixels[yi + 2] = Math.round(bsum / div / div);

      if (x === 0) {
        vmin[y] = Math.min(y + radius + 1, hm) * width;
        vmax[y] = Math.max(y - radius, 0) * width;
      }

      p1 = x + vmin[y];
      p2 = x + vmax[y];

      rsum += r[p1] - r[p2];
      gsum += g[p1] - g[p2];
      bsum += b[p1] - b[p2];

      yi += width << 2;
    }
  }
};

/**
 * 生成模糊圖片
 * 使用 StackBlur 算法，100% 跨瀏覽器兼容（包含 Safari）
 * @param {File} file - 原始圖片檔案
 * @returns {Promise<File>} - 返回模糊處理後的圖片檔案（保持原尺寸，只做模糊效果）
 */
export const generateBlurredImage = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.src = reader.result;
      img.onload = () => {
        // 同 processImage：onload 內同步丟出的例外要自己接住，否則 Promise 永遠不結束
        try {
          const blurRadius = 10;

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;

          ctx.drawImage(img, 0, 0, img.width, img.height);

          // 使用 StackBlur 算法處理像素
          // 執行 3 次以趨近高斯模糊效果
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          stackBlur(imageData, blurRadius);
          stackBlur(imageData, blurRadius);
          stackBlur(imageData, blurRadius);
          ctx.putImageData(imageData, 0, 0);

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to generate blurred image'));
              return;
            }
            resolve(new File([blob], `blur_${file.name}`, { type: file.type }));
          }, file.type, 0.7);
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = () => {
        reject(new Error('Image loading failed'));
      };
    };
    reader.onerror = () => {
      reject(new Error('File reading failed'));
    };
    reader.readAsDataURL(file);
  });
};